<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { debounceTime, fromEvent } from 'rxjs';
import type { Subscription } from 'rxjs';

import type { AppInputTheme } from './AppInput.model';

export interface AppInputProps {
  inputText: string;
  placeholder: string;
  theme: AppInputTheme;
  shoulDisplayMobileVersion?: boolean;
  maxLength?: number;
  debounceDelay?: number;
}

const props = defineProps<AppInputProps>();

interface AppInputEmits {
  (event: 'text-changed', value: string): void;
  (event: 'focus-changed', value: boolean): void;
}
const emit = defineEmits<AppInputEmits>();

const inputRef = ref<HTMLInputElement | null>(null);
let inputSubscription: Subscription | null = null;
const DEFAULT_DEBOUNCE_TIME = 0;

onMounted(() => {
  if (inputRef.value) {
    inputSubscription = fromEvent(inputRef.value, 'input')
      .pipe(debounceTime(props.debounceDelay ?? DEFAULT_DEBOUNCE_TIME))
      .subscribe((event) => {
        const target = event.target as HTMLInputElement;
        emit('text-changed', target.value);
      });
  }
});

onBeforeUnmount(() => {
  if (inputSubscription) {
    inputSubscription.unsubscribe();
  }
});
</script>

<template>
  <div
    class="app-input"
    :class="{
      'app-mobile-input': props.shoulDisplayMobileVersion,
      [props.theme]: true,
    }"
  >
    <slot name="left-icon" />
    <input
      ref="inputRef"
      :value="props.inputText"
      type="text"
      :maxlength="props.maxLength"
      :placeholder="props.placeholder"
      @focus="emit('focus-changed', true)"
      @blur="emit('focus-changed', false)"
    />
    <slot name="right-icon" />
  </div>
</template>

<style scoped lang="scss">
@import '../../styles/_fonts.scss';
.app-input {
  position: relative;
  box-sizing: border-box;
  border-radius: var(--border-radius-12);
  overflow: hidden;
  user-select: none;
  display: flex;
  align-items: center;
  margin: 0;
  height: 44px;
  padding: 0 0 0 var(--spacing-16);
  width: 100%;
  gap: var(--spacing-12);

  &:has(input:focus) {
    box-shadow: inset 0 0 0 1px var(--color-soft-black);
  }

  &.app-mobile-input {
    transition: all var(--transition-very-short);
    min-width: 180px;
    width: 180px;

    &:has(input:focus) {
      min-width: 280px;
    }
  }

  &.light {
    background: var(--color-white);
  }

  &.dark {
    background: var(--color-neutral-30);
  }
  &.primary {
    background: var(--color-white);
    border: 1px solid var(--color-brand-violet);
    border-radius: var(--border-radius-8) var(--border-radius-8) 0 0;
    &:has(input:focus) {
      box-shadow: none;
    }
  }

  input {
    background: inherit;
    color: var(--color-neutral-70);
    width: 100%;
    @include font-label-m-light;
    border-radius: var(--border-radius-12);
    border: none;
    outline: 0;
    box-shadow: none;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    margin-right: var(--spacing-16);
    padding-right: var(--spacing-24);
    &:focus {
      &::placeholder {
        color: var(--color-neutral-50);
      }
    }

    &::placeholder {
      color: var(--color-neutral-70);
      @include font-label-m-light;
    }
  }
}
</style>
